import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks/useAxiosClient";
import {
  ArchiveStudyRequest,
  GetStudiesRequest,
  TransferStudyRequest,
} from "@/types/requests";
import { Run, Study } from "@/types/resources";
import {
  GetRunsResponse,
  GetStudiesResponse,
  TransferStudyResponse,
  UpdateStudyResponse,
} from "@/types/responses";

export const useStudiesByUser = (
  userId: string,
  search: string = "",
  itemsPerPage: number = 10,
  createdAfter?: string,
  createdBefore?: string
) => {
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [studies, setStudies] = useState<Study[]>([]);

  const client = useAxiosClient("/studies");

  const getStudies = useCallback(
    (p: number) => {
      const params: GetStudiesRequest = {
        created_after: createdAfter,
        created_before: createdBefore,
        created_by_id: userId,
        items_per_page: itemsPerPage,
        name: search || undefined,
        page: p,
      };

      client
        .get<GetStudiesResponse>("", { params })
        .then(({ data }) => {
          setCount(data.count);
          setStudies((prev) =>
            p === 1 ? data.studies : [...prev, ...data.studies]
          );
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : "Unknown error");
        })
        .finally(() => {
          if (p === 1) setLoading(false);
          else setLoadingMore(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, itemsPerPage, search, createdAfter, createdBefore]
  );

  // Reset the list during render (not in an effect) when the query inputs
  // change, so the UI shows a fresh loading state while refetching.
  const inputsKey = JSON.stringify([
    userId,
    itemsPerPage,
    search,
    createdAfter,
    createdBefore,
  ]);
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setStudies([]);
    setPage(1);
    setLoading(true);
    setError(null);
  }

  useEffect(() => {
    getStudies(1);
  }, [getStudies]);

  const loadMore = useCallback(() => {
    const next = page + 1;
    setLoadingMore(true);
    setPage(next);
    getStudies(next);
  }, [getStudies, page]);

  return {
    count,
    error,
    hasMore: studies.length < count,
    loading,
    loadingMore,
    loadMore,
    setCount,
    setStudies,
    studies,
  };
};

export const useStudyRuns = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState<Run[]>([]);

  const client = useAxiosClient("/studies");

  const fetch = useCallback(() => {
    client
      .get<GetRunsResponse>(`/${studyId}/runs`)
      .then(({ data }) => {
        setRuns(data.runs);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Unknown error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, studyId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  return { error, loading, refresh, runs };
};

export const useTransferStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/studies");

  const transferStudy = useCallback(
    async (studyId: string, userId: string) => {
      setError(null);
      setLoading(true);
      try {
        const data: TransferStudyRequest = { user_id: userId };
        const response = await client.post<TransferStudyResponse>(
          `/${studyId}/transfer`,
          data
        );
        return response.data.study;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { error, loading, transferStudy };
};

export const useArchiveStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/studies");

  const archiveStudy = useCallback(
    async (studyId: string, archived: boolean) => {
      setError(null);
      setLoading(true);
      try {
        const data: ArchiveStudyRequest = { archived };
        const response = await client.patch<UpdateStudyResponse>(
          `/${studyId}`,
          data
        );
        return response.data.study;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { archiveStudy, error, loading };
};

export const useDeleteStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/studies");

  const deleteStudy = useCallback(
    async (studyId: string) => {
      setError(null);
      setLoading(true);
      try {
        await client.delete(`/${studyId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { deleteStudy, error, loading };
};
